import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  //   Alert,
} from "@mui/material";
// import { Add, Delete, Download } from '@mui/icons-material';
import { Add, Delete } from "@mui/icons-material";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_CERTIFICATES_BY_EMPLOYEE,
  CREATE_CERTIFICATION_INFO,
  DELETE_CERTIFICATION_INFO,
  Certification,
} from "../../graphql/operations";
import { PREDEFINED_CERTIFICATIONS } from "../../utils/constants";

interface CertificationFormProps {
  employeeId: string;
  isEdit: boolean;
}

const CertificationSection: React.FC<CertificationFormProps> = ({
  employeeId,
  isEdit,
}) => {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [customCertName, setCustomCertName] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [newCertification, setNewCertification] = useState({
    name: "",
    fileName: "",
    fileUrl: "",
    issueDate: new Date().toISOString().split("T")[0],
    expirationDate: "",
  });

  // Fetch certifications
  //   const { data, loading, error, refetch } = useQuery(GET_CERTIFICATES_BY_EMPLOYEE, {
  const { data, refetch } = useQuery(GET_CERTIFICATES_BY_EMPLOYEE, {
    variables: { employeeId },
    skip: !employeeId,
  });

  const [createCertification] = useMutation(CREATE_CERTIFICATION_INFO);
  const [deleteCertification] = useMutation(DELETE_CERTIFICATION_INFO);

  useEffect(() => {
    if (data?.getCertificatesByEmployee) {
      setCertifications(data.getCertificatesByEmployee);
    }
  }, [data]);

  const handleAddCertification = async () => {
    try {
      // Use custom name if provided, otherwise use selected name
      // const certificationName =
      //   showCustomInput && customCertName
      //     ? customCertName
      //     : newCertification.name;

      await createCertification({
        variables: {
          employeeId,
          input: newCertification,
        },
      });
      setOpenDialog(false);
      setNewCertification({
        name: "",
        fileName: "",
        fileUrl: "",
        issueDate: new Date().toISOString().split("T")[0],
        expirationDate: "",
      });
      setCustomCertName("");
      setShowCustomInput(false);
      refetch();
    } catch (err) {
      console.error("Error creating certification:", err);
    }
  };

  const handleCertificationNameChange = (event: any) => {
    const value = event.target.value;
    if (value === "custom") {
      setShowCustomInput(true);
      setNewCertification({ ...newCertification, name: "" });
    } else {
      setShowCustomInput(false);
      setCustomCertName("");
      setNewCertification({ ...newCertification, name: value });
    }
  };

  const handleDeleteCertification = async (certificationId: string) => {
    try {
      await deleteCertification({
        variables: {
          employeeId,
          id: certificationId,
        },
      });
      refetch();
    } catch (err) {
      console.error("Error deleting certification:", err);
    }
  };

  //   const handleDownload = (fileUrl: string, fileName: string) => {
  const handleDownload = (fileUrl: string) => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    }
  };

  if (!isEdit) {
    // Read-only view for non-edit mode
    return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Certifications
        </Typography>
        {certifications.length === 0 ? (
          <Typography color="textSecondary">No certifications</Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {certifications.map((cert) => (
              <Chip
                key={cert.id}
                label={cert.name}
                variant="outlined"
                onDelete={undefined} // No delete in read-only
                // onClick={cert.fileUrl ? () => handleDownload(cert.fileUrl ?? '', cert.fileName ?? '') : undefined}
                onClick={
                  cert.fileUrl
                    ? () => handleDownload(cert.fileUrl ?? "")
                    : undefined
                }
              />
            ))}
          </Box>
        )}
      </Paper>
    );
  }

  // Edit mode view
  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Certifications</Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
        >
          Add Certification
        </Button>
      </Box>

      {certifications.length === 0 ? (
        <Typography color="textSecondary">
          No certifications added yet
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {certifications.map((cert) => (
            <Paper key={cert.id} variant="outlined" sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle1">{cert.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Issued: {new Date(cert.issueDate).toLocaleDateString()}
                    {cert.expirationDate &&
                      ` • Expires: ${new Date(
                        cert.expirationDate
                      ).toLocaleDateString()}`}
                  </Typography>
                  {cert.fileName && (
                    <Typography variant="body2" color="textSecondary">
                      File: {cert.fileName}
                    </Typography>
                  )}
                </Box>
                <Box>
                  {/* {cert.fileUrl && (
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(cert.fileUrl ?? '', cert.fileName ?? '')}
                      title="Download"
                    >
                      <Download />
                    </IconButton>
                  )} */}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteCertification(cert.id)}
                    title="Delete"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Add Certification Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Certification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Certification Name</InputLabel>
              <Select
                value={showCustomInput ? "custom" : newCertification.name}
                onChange={handleCertificationNameChange}
                label="Certification Name"
                required
              >
                <MenuItem value="">
                  <em>Select a certification</em>
                </MenuItem>
                {PREDEFINED_CERTIFICATIONS.map((cert) => (
                  <MenuItem key={cert} value={cert}>
                    {cert}
                  </MenuItem>
                ))}
                {/* <MenuItem value="custom">
                  <em>+ Custom Certification</em>
                </MenuItem> */}
              </Select>
            </FormControl>

            {showCustomInput && (
              <TextField
                label="Custom Certification Name"
                value={customCertName}
                onChange={(e) => setCustomCertName(e.target.value)}
                required
                placeholder="Enter custom certification name"
              />
            )}
            {/* <TextField
              label="File Name (optional)"
              value={newCertification.fileName}
              onChange={(e) => setNewCertification({ ...newCertification, fileName: e.target.value })}
            />
            <TextField
              label="File URL (optional)"
              value={newCertification.fileUrl}
              onChange={(e) => setNewCertification({ ...newCertification, fileUrl: e.target.value })}
            /> */}
            <TextField
              label="Issue Date"
              type="date"
              value={newCertification.issueDate}
              onChange={(e) =>
                setNewCertification({
                  ...newCertification,
                  issueDate: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="Expiration Date (optional)"
              type="date"
              value={newCertification.expirationDate}
              onChange={(e) =>
                setNewCertification({
                  ...newCertification,
                  expirationDate: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddCertification}
            variant="contained"
            disabled={
              (!newCertification.name && !customCertName) || 
              !newCertification.issueDate ||
              (showCustomInput && !customCertName)
            }
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CertificationSection;
